/* eslint-disable no-nested-ternary */
import React, { FC } from "react";
import Head from "next/head";
import { classNames } from "repoV2/utils/common/render/classNames";
import {
    EVENT_TYPE,
    LISTING_MISCELLANEOUS_DATA_TYPES,
    NEED_USER_INPUT_TO_PROCEED_KEY,
    isBrowser,
    useDataRead,
} from "@Utils";
import dynamic from "next/dynamic";
import { CustomCodeBase } from "@Modules/customCode/CustomCode";
import { CUSTOM_CODES_POSITION } from "repoV2/constants/customCodes/customCodes.constants";
import { useCustomCode } from "@Modules/eventPage/utils/useCustomCode";
import { useEvaluatePaymentPrice } from "@Modules/eventPage/utils/useEvaluatePaymentPrice";
import { IEventPage } from "@Interfaces";
import { ICompleteEventPageModule } from "./ICompleteEventPageModule";
import {
    DiscountField,
    EventMedia,
    ContentHeader,
    AboutCreator,
    AboutEvent,
    CreatorFAQ,
    CourseDetails,
    Reviews,
    AddressInfo,
    RecentSalesPopup,
    LoyaltyDiscountsPopup,
    OfferPromotion,
    AboutPlan,
} from "..";
import { SelectTimezone } from "../../../common";
import eventStyles from "../../event.module.scss";
import commonStyles from "../commonStyles.module.scss";
import RenewBookingForm from "../RenewBookingForm";
import CTAButtons from "../CTAButtons";
import PlanOverhang from "../PlanOverhang";
import TicketCount from "../TicketCount";
import MobileFooter from "../Footers/MobileFooter";
import EarlyBird from "../EarlyBird";
import DetailsSectionFooter from "../Footers/DetailsSectionFooter";
import ScheduleSelector from "../ScheduleSelector";
import UpsellListings from "../UpsellListings";
import TrustIcons from "../TrustIcons";
import { useFetchMiscellaneousData } from "../MiscellaneousData/data/useFetchMiscellaneousData";
import ExpressInterest from "../ExpressInterest";
import { usePlanOverhangContainer } from "../PlanOverhang/usePlanOverhangContainer";
import { SidebarSectionDivider } from "../SidebarSectionDivider";
import { DetailsSectionDivider } from "../DetailsSectionDivider";
import { getHidePaymentSummary } from "../PaymentSummary/utils/getHidePaymentSummary";
import VariantSelector from "../VariantSelector/VariantSelector";
import CheckAvailability from "../CheckAvailability/CheckAvailability";
import styles from "./CompleteEventPage.module.scss";
import Curriculum from "../Curriculum";
import Certificate from "../Certificate";
import LazyLoadedPaymentPlan from "../PaymentPlan/LazyLoadedPaymentPlan";
import usePaymentSummaryData from "../PaymentSummary/utils/usePaymentSummaryData";
import { PaymentSummaryWithDiscount } from "../PaymentSummary/components/PaymentSummaryWithDiscount";
import { OfferingVariationSelector } from "../OfferingVariationSelector/OfferingVariationSelector";

const PlanDropdown = dynamic(() => import("../PlanDropdown"));
const BrandedCommunityOfferings = dynamic(
    () => import("../BrandedCommunityOfferings")
);
// Original dev's note:
// A brief on how this module has been built
// https://img.devrant.com/devrant/rant/r_3898730_rZ4yd.jpg

const CompleteEventPage: FC<ICompleteEventPageModule.IProps> = props => {
    const {
        // If this hideSelf prop is present, then the event page is open as a modal
        hideSelf,
        showOpaqueBackdrop,
        mainScrolledComponentRef,
        isMobile,
        eventData,
        eventMediaProps,
        eventType,
        hostData,
        hideTillOfferingNotSelectedInPlan,
        hideTillVariantNotSelected,
        planDropdownProps,
        disablePayment,
        isSoldOut,
        hidePrice,
        scheduleSelectorProps,
        earlyBirdObj,
        recentSalesPopupProps,
        ctaButtonProps,
        ticketCountProps,
        price,
        paymentSummaryProps,
        urlQueryParams,
        isEventModal,
        // props calculated in the parent component
        aboutCreatorProps,
        aboutEventProps,
        aboutPlanProps,
        addressInfoProps,
        contentHeaderProps,
        creatorFAQProps,
        discountProps,
        earlyBirdProps,
        offerPromotionProps,
        expressInterestProps,
        reviewsProps,
        slots,
    } = props;

    const customCodeProps = useCustomCode();
    const hidePaymentSummary = getHidePaymentSummary({
        disablePayment,
        eventData,
    });
    const {
        planDropdownRef,
        handlePlanOverhangContainerScroll,
        planOverhangProps,
        showPlanOverhang,
    } = usePlanOverhangContainer({ urlQueryParams });
    useFetchMiscellaneousData({ listingUuid: eventData?.uuid });

    // logic for `hideScheduleOnDesktopInDetailsBody` and `hideScheduleOnMobileInSidebar` starts.

    /**
    TODO:
     - componnets that are put under the same umbrella as these variable should individually handle render condition using:
     - `showOn` and `isMobile`
     */
    /**
    Inputs will be shown on (desktop main body OR mobile main body) if:
    - Payment is enabled
    - Event type is workshop
    */
    const hideScheduleOnDesktopInDetailsBody =
        hidePrice || (!isMobile && eventType !== EVENT_TYPE.WORKSHOPS);
    /**
    Inputs will be hidden on the desktop sidebar if:
    - Payment is disabled
    - Event type is workshop
    */
    const hideScheduleOnMobileInSidebar: boolean =
        hidePrice || isMobile || eventType === EVENT_TYPE.WORKSHOPS;

    // logic for `hideScheduleOnDesktopInDetailsBody` and `hideScheduleOnMobileInSidebar` ends.

    const { hideComponent, evaluatePaymentPriceProps } =
        usePaymentSummaryData(paymentSummaryProps);

    const {
        renderLeft: evaluatePricePerItemsLeft = <></>,
        renderRight: evaluatePricePerItemsRight = <></>,
    } = useEvaluatePaymentPrice(
        evaluatePaymentPriceProps
    ) as IEventPage.IEvaluatePaymentPriceResult;

    const EventMediaComponent = () => <EventMedia {...eventMediaProps} />;

    const paymentPerItemElement = ({ classname }: { classname: string }) => (
        <>
            <div className={classname}>
                {/* Incase hide_payment_summary check is true, 
                    then we do not have to show this section */}
                {!hideComponent && evaluatePricePerItemsLeft}
                <span className="flexGrow" />
                {/* Incase hide_payment_summary check is true, 
                    then we do not have to show this section */}
                {!hideComponent && evaluatePricePerItemsRight}
            </div>
            {isMobile || hidePaymentSummary ? null : <SidebarSectionDivider />}
        </>
    );

    const userInputInitiated = useDataRead(
        NEED_USER_INPUT_TO_PROCEED_KEY
    )?.initiated;

    return (
        <>
            <Head>
                <meta
                    name="viewport"
                    content="width=device-width, initial-scale=1.0, minimum-scale=1.0, maximum-scale=1.0, user-scalable=no"
                />
            </Head>
            <div
                className={classNames(
                    eventStyles.root,
                    // // To hide the base event page modal when another modal is opened.
                    // // Not for when event page is opened with a dedicated url
                    // hideSelf && isModalOpen && modalType ? styles.hide : "",

                    // If the event page is opened as a modal, add the isModal css class
                    hideSelf ? eventStyles.isModal : "",
                    showOpaqueBackdrop && eventStyles.opaqueBackdrop
                )}
            >
                <div className={eventStyles.container}>
                    <div
                        className={eventStyles.main}
                        ref={mainScrolledComponentRef}
                    >
                        {!isMobile && isBrowser() && (
                            <ContentHeader
                                showOn="desktop"
                                {...contentHeaderProps}
                            />
                        )}
                        <div
                            className={classNames(
                                eventStyles.body,
                                commonStyles.detailsSectionsContainer
                            )}
                            onScroll={handlePlanOverhangContainerScroll}
                        >
                            {isMobile && isBrowser() && (
                                <ContentHeader
                                    showOn="mobile"
                                    EventMedia={EventMediaComponent}
                                    {...contentHeaderProps}
                                />
                            )}

                            {showPlanOverhang && isMobile ? (
                                <PlanOverhang {...planOverhangProps} />
                            ) : null}

                            {/* @doubleRendering: DONT Restructure the top two elements */}
                            {!isSoldOut && (
                                <div className={styles.inputsContainer}>
                                    <PlanDropdown
                                        planDropdownRef={planDropdownRef}
                                        showOn="mobile"
                                        {...planDropdownProps}
                                    />

                                    {isMobile &&
                                        eventData.is_offering_variants_enabled &&
                                        userInputInitiated && (
                                            <OfferingVariationSelector />
                                        )}

                                    {hideScheduleOnDesktopInDetailsBody ? null : (
                                        <>
                                            <VariantSelector
                                                className={
                                                    commonStyles.variantSelector
                                                }
                                            />

                                            <LazyLoadedPaymentPlan
                                                earlyBirdProps={earlyBirdProps}
                                                paymentSummaryProps={
                                                    paymentSummaryProps
                                                }
                                            />

                                            <ScheduleSelector
                                                {...scheduleSelectorProps}
                                                titleClass={
                                                    commonStyles.detailsSectionTitle
                                                }
                                            />
                                            <CheckAvailability
                                                disablePayment={disablePayment}
                                                className={
                                                    commonStyles.checkAvailability
                                                }
                                            />
                                            <DetailsSectionDivider
                                                className={
                                                    styles.mobileInputsDivider
                                                }
                                            />
                                        </>
                                    )}
                                    {hideTillOfferingNotSelectedInPlan ||
                                    hideTillVariantNotSelected ? null : (
                                        <>
                                            <DiscountField
                                                showOn="mobile"
                                                {...discountProps.discountFieldProps}
                                            />
                                            <OfferPromotion
                                                showOn="mobile"
                                                {...offerPromotionProps}
                                            />
                                        </>
                                    )}
                                </div>
                            )}
                            {isMobile && !isSoldOut ? (
                                <SelectTimezone
                                    alwaysRefetch={{
                                        event: true,
                                        host: !!hideSelf,
                                    }}
                                />
                            ) : null}
                            <CustomCodeBase
                                {...customCodeProps}
                                position={
                                    CUSTOM_CODES_POSITION.MAIN_CONTENT_START
                                }
                            />

                            <AboutPlan {...aboutPlanProps} />

                            <AboutEvent {...aboutEventProps} />

                            <AddressInfo {...addressInfoProps} />

                            <Curriculum />

                            {/* TODO: Add metadata override-ability */}
                            <CourseDetails
                                courseDetail={
                                    LISTING_MISCELLANEOUS_DATA_TYPES.highlights
                                }
                                title={
                                    eventData?.metadata
                                        ?.listing_highlights_title ||
                                    LISTING_MISCELLANEOUS_DATA_TYPES.highlights
                                        .title
                                }
                            />

                            <RenewBookingForm
                                showOn="mobile"
                                isMobile={isMobile}
                            />
                            <BrandedCommunityOfferings
                                isEventModal={isEventModal}
                            />

                            {/* TODO: Add metadata override-ability */}
                            <CourseDetails
                                courseDetail={
                                    LISTING_MISCELLANEOUS_DATA_TYPES.requirements
                                }
                                title={
                                    eventData?.metadata
                                        ?.listing_requirements_title ||
                                    LISTING_MISCELLANEOUS_DATA_TYPES
                                        .requirements.title
                                }
                            />

                            <Certificate />

                            <TrustIcons />

                            <UpsellListings />

                            <Reviews {...reviewsProps} />

                            <AboutCreator {...aboutCreatorProps} />

                            {hostData?.cancellation_policy ? (
                                <div className={eventStyles.section}>
                                    <div className={eventStyles.sectionTitle}>
                                        Cancellation Policy
                                    </div>
                                    {/* TODO: Insert data from API */}
                                    <div className={eventStyles.sectionBody}>
                                        {hostData?.cancellation_policy}
                                    </div>
                                </div>
                            ) : null}
                            <CreatorFAQ {...creatorFAQProps} />
                            <CustomCodeBase
                                {...customCodeProps}
                                position={
                                    CUSTOM_CODES_POSITION.MAIN_CONTENT_END
                                }
                            />
                            <RecentSalesPopup {...recentSalesPopupProps} />
                        </div>
                        <DetailsSectionFooter isMobile={isMobile} />
                    </div>
                    {!isMobile && isBrowser() ? (
                        <div className={eventStyles.sidebar}>
                            <EventMediaComponent />

                            <div
                                className={eventStyles.paymentSummary}
                                style={{
                                    display: "flex",
                                }}
                            >
                                <EarlyBird {...earlyBirdProps} />

                                <TicketCount
                                    {...ticketCountProps}
                                    bottomSibling={<SidebarSectionDivider />}
                                />

                                <PlanDropdown
                                    showOn="desktop"
                                    {...planDropdownProps}
                                />
                                {eventData.is_offering_variants_enabled &&
                                    userInputInitiated && (
                                        <OfferingVariationSelector />
                                    )}

                                {!isSoldOut && (
                                    <SelectTimezone
                                        alwaysRefetch={{
                                            event: true,
                                            host: !!hideSelf,
                                        }}
                                    />
                                )}

                                <VariantSelector showBottonDivider />

                                <LazyLoadedPaymentPlan
                                    earlyBirdProps={earlyBirdProps}
                                    paymentSummaryProps={paymentSummaryProps}
                                />

                                <RenewBookingForm
                                    showOn="desktop"
                                    isMobile={isMobile}
                                />

                                {hideScheduleOnMobileInSidebar ? null : (
                                    <>
                                        <ScheduleSelector
                                            {...scheduleSelectorProps}
                                            titleClass={
                                                commonStyles.sidebarSectionTitle
                                            }
                                        />
                                        <SidebarSectionDivider />
                                    </>
                                )}

                                <ExpressInterest
                                    showOn="desktop"
                                    {...expressInterestProps}
                                />

                                <CheckAvailability
                                    disablePayment={disablePayment}
                                    showBottonDivider
                                />
                                {!disablePayment &&
                                    eventType === EVENT_TYPE.APPOINTMENTS &&
                                    paymentPerItemElement({
                                        classname: eventStyles.priceDesc,
                                    })}

                                <PaymentSummaryWithDiscount
                                    paymentSummaryProps={paymentSummaryProps}
                                    eventData={eventData}
                                    eventType={eventType}
                                    slots={slots}
                                    discountProps={discountProps}
                                    offerPromotionProps={offerPromotionProps}
                                />

                                {!disablePayment && (
                                    <>
                                        <br />
                                        <CTAButtons {...ctaButtonProps} />
                                    </>
                                )}
                            </div>
                        </div>
                    ) : null}

                    {!disablePayment && isMobile && (
                        <MobileFooter>
                            <EarlyBird {...earlyBirdProps} />
                            {!slots?.length &&
                                eventType === EVENT_TYPE.APPOINTMENTS &&
                                paymentPerItemElement({
                                    classname: `${eventStyles.priceDesc} ${styles.mobileFooterUpperHalf}`,
                                })}
                            {hideTillOfferingNotSelectedInPlan ||
                            hideTillVariantNotSelected ||
                            hidePaymentSummary ||
                            (!earlyBirdObj && price === 0) ? null : (
                                <PaymentSummaryWithDiscount
                                    paymentSummaryProps={paymentSummaryProps}
                                    eventData={eventData}
                                    eventType={eventType}
                                    className={styles.mobileFooterUpperHalf}
                                    slots={slots}
                                    hideDiscountField
                                    hideOfferPromotion
                                    isHidePaymentSummary
                                    discountProps={discountProps}
                                    offerPromotionProps={offerPromotionProps}
                                />
                            )}

                            <div className={styles.mobileFooterLowerHalf}>
                                <TicketCount {...ticketCountProps} />

                                <CTAButtons {...ctaButtonProps} />
                            </div>
                        </MobileFooter>
                    )}
                    <ExpressInterest
                        showOn="mobile"
                        {...expressInterestProps}
                    />
                </div>
                {/* cannot move `hideComponent` statement inside the component as open/close
                popup animation starts behaving weirdly */}
                {!discountProps.loyaltyDiscountsPopupProps.hideComponent ? (
                    <LoyaltyDiscountsPopup
                        {...discountProps.loyaltyDiscountsPopupProps}
                    />
                ) : null}
            </div>
        </>
    );
};

export default CompleteEventPage;
// The goal should be to keep this page length < 1000 lines
